import { apiRequest } from './api';

// ==================== TYPES ====================

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'prova_published' | 'correcao_new' | 'turma_update' | 'system' | 'promo';
  is_read: boolean;
  action_url?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_enabled: boolean;
  push_enabled: boolean;
  prova_published: boolean;
  correcao_new: boolean;
  turma_update: boolean;
  system_alerts: boolean;
  weekly_report: boolean;
  promo_notifications: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  updated_at: string;
}

// ==================== NOTIFICATIONS ====================

export async function getNotifications(limit = 50): Promise<Notification[]> {
  return apiRequest<Notification[]>(`/notifications?limit=${limit}`);
}

export async function getUnreadCount(): Promise<number> {
  const result = await apiRequest<{ count: number }>('/notifications/unread-count');
  return result.count;
}

export async function markAsRead(notificationId: string): Promise<void> {
  await apiRequest(`/notifications/${notificationId}/read`, { method: 'PUT' });
}

export async function markAllAsRead(): Promise<void> {
  await apiRequest('/notifications/read-all', { method: 'PUT' });
}

export async function deleteNotification(notificationId: string): Promise<void> {
  await apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
}

export async function clearAllNotifications(): Promise<void> {
  await apiRequest('/notifications/clear', { method: 'DELETE' });
}

// ==================== PREFERENCES ====================

export async function getNotificationPreferences(): Promise<NotificationPreferences | null> {
  try {
    return await apiRequest<NotificationPreferences>('/notifications/preferences');
  } catch {
    return null;
  }
}

export async function upsertNotificationPreferences(
  prefs: Partial<Omit<NotificationPreferences, 'id' | 'user_id' | 'updated_at'>>
): Promise<NotificationPreferences> {
  return apiRequest<NotificationPreferences>('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

// ==================== ADMIN NOTIFICATIONS ====================

export async function adminSendNotification(data: {
  title: string;
  message: string;
  type?: string;
  target: 'all' | 'plan' | 'user';
  target_value?: string;
  action_url?: string;
}): Promise<{ sent: number }> {
  return apiRequest<{ sent: number }>('/admin/notifications/send', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminGetNotificationLog(limit = 50) {
  return apiRequest<any[]>(`/admin/notifications/log?limit=${limit}`);
}

export async function adminSendPromos() {
  return apiRequest<{ sent: number }>('/admin/notifications/send-promos', { method: 'POST' });
}

export async function adminSearchUsers(query: string) {
  return apiRequest<any[]>(`/admin/users/search?q=${encodeURIComponent(query)}`);
}

// ==================== PUSH TOKENS ====================

export async function registerPushToken(token: string, platform: string, deviceName?: string) {
  return apiRequest('/notifications/push-token', {
    method: 'POST',
    body: JSON.stringify({ token, platform, device_name: deviceName }),
  });
}

export async function removePushToken(token: string) {
  return apiRequest('/notifications/push-token', {
    method: 'DELETE',
    body: JSON.stringify({ token }),
  });
}
